/**
 * TruSkin FGWP (Free Gift With Purchase) Middleware
 * Automatically adds/removes free gift variants based on cart state and promotion schedule
 */

(function() {
  'use strict';

  const FGWP_PROPERTY = '_free_gift';
  let isProcessing = false;

  /**
   * Calculate Eastern timezone offset with DST detection
   * DST Rules: Second Sunday in March (2am) to First Sunday in November (2am)
   */
  function getEasternOffset(date) {
    const year = date.getFullYear();

    // Calculate second Sunday in March
    const marchFirst = new Date(year, 2, 1);
    const dstStart = new Date(year, 2, 14 - marchFirst.getDay());

    // Calculate first Sunday in November
    const novemberFirst = new Date(year, 10, 1);
    const dstEnd = new Date(year, 10, 7 - novemberFirst.getDay());

    const isDST = date >= dstStart && date < dstEnd;
    return isDST ? -4 : -5; // EDT (-4) or EST (-5)
  }

  /**
   * Parse Eastern timezone datetime string to UTC timestamp
   * Format: YYYY-MM-DD HH:MM or YYYY-MM-DD HH:MM:SS (24-hour format)
   */
  function parseEasternDateTime(dateString) {
    if (!dateString) return null;

    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2})(?::\d{2})?$/);
    if (!match) {
      console.warn('FGWP: Invalid date format:', dateString);
      return null;
    }

    const [, year, month, day, hour, minute] = match;
    const easternDate = new Date(year, month - 1, day, hour, minute);
    const offset = getEasternOffset(easternDate);

    // Convert to UTC timestamp
    return easternDate.getTime() - (offset * 60 * 60 * 1000);
  }

  /**
   * Check if FGWP promotion is currently active
   */
  function isActive() {
    const settings = window.truskinSettings?.fgwp;

    if (!settings || !settings.enabled) {
      return false;
    }

    const startTime = parseEasternDateTime(settings.startDateTime);
    if (!startTime) {
      return false;
    }

    const endTime = parseEasternDateTime(settings.endDateTime);
    const now = Date.now();

    // Check if current time is within promotion window
    return now >= startTime && (!endTime || now <= endTime);
  }

  /**
   * Check if cart has qualifying items (non-gift paid items)
   */
  function cartHasQualifyingItems(cart) {
    if (!cart || !cart.items) return false;

    return cart.items.some(function(item) {
      // Skip gift items
      if (item.properties && item.properties[FGWP_PROPERTY]) {
        return false;
      }
      // Check for paid items
      return item.price > 0;
    });
  }

  /**
   * Parse variant IDs from settings
   */
  function getGiftVariantIds() {
    const variantIds = window.truskinSettings?.fgwp?.variantIds || '';
    if (!variantIds) return [];

    return variantIds.split(',')
      .map(function(id) { return parseInt(id.trim()); })
      .filter(function(id) { return !isNaN(id); });
  }

  /**
   * Check if cart already has a specific gift variant
   */
  function cartHasGift(cart, variantId) {
    if (!cart || !cart.items) return false;

    return cart.items.some(function(item) {
      return item.variant_id === variantId &&
             item.properties &&
             item.properties[FGWP_PROPERTY];
    });
  }

  /**
   * Check if cart has an orphaned gift (variant without _free_gift property)
   */
  function cartHasOrphanedGift(cart, variantId) {
    if (!cart || !cart.items) return false;

    return cart.items.some(function(item) {
      return item.variant_id === variantId &&
             (!item.properties || !item.properties[FGWP_PROPERTY]);
    });
  }

  /**
   * Main cart change handler
   * Adds or removes gifts based on cart state and promotion status
   */
  function handleCartChange(cart) {
    console.log('FGWP: handleCartChange called', {
      isProcessing: isProcessing,
      cartItems: cart?.items?.length || 0
    });

    // Re-entrancy guard
    if (isProcessing) {
      console.log('FGWP: Already processing, skipping...');
      return;
    }

    isProcessing = true;
    var actionTaken = false;
    var isCartPage = window.location.pathname === '/cart';

    try {
      const giftVariantIds = getGiftVariantIds();
      console.log('FGWP: Gift variant IDs:', giftVariantIds);

      if (giftVariantIds.length === 0) {
        console.warn('FGWP: No gift variant IDs configured');
        return;
      }

      // Check for and remove orphaned gifts (gifts without _free_gift property)
      giftVariantIds.forEach(function(variantId) {
        if (cartHasOrphanedGift(cart, variantId)) {
          console.log('FGWP: Found orphaned gift (missing property), removing variant', variantId);
          actionTaken = true;
          var orphanedItem = null;
          for (var i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            if (item.variant_id === variantId &&
                (!item.properties || !item.properties[FGWP_PROPERTY])) {
              orphanedItem = item;
              break;
            }
          }

          if (orphanedItem) {
            CartJS.removeItemById(orphanedItem.id, {
              success: function(data) {
                console.log('FGWP: Orphaned gift removed successfully');
                if (typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
                  reloadAjaxCartItemUsingCartAjaxObject(data);
                }
                if (isCartPage) {
                  console.log('FGWP: Reloading cart page after removing orphaned gift');
                  setTimeout(function() {
                    window.location.reload();
                  }, 500);
                }
              }
            });
          }
        }
      });

      const hasQualifyingItems = cartHasQualifyingItems(cart);
      const promoIsActive = isActive();
      const shouldHaveGifts = promoIsActive && hasQualifyingItems;

      console.log('FGWP: Check results:', {
        hasQualifyingItems: hasQualifyingItems,
        promoIsActive: promoIsActive,
        shouldHaveGifts: shouldHaveGifts,
        isCartPage: isCartPage
      });

      giftVariantIds.forEach(function(variantId) {
        const hasGift = cartHasGift(cart, variantId);
        console.log('FGWP: Checking variant ' + variantId + ':', {
          hasGift: hasGift,
          shouldAdd: shouldHaveGifts && !hasGift,
          shouldRemove: !shouldHaveGifts && hasGift
        });

        if (shouldHaveGifts && !hasGift) {
          // Add gift to cart
          console.log('FGWP: Adding gift variant', variantId);
          actionTaken = true;
          var properties = {};
          properties[FGWP_PROPERTY] = "true";

          CartJS.addItem(variantId, 1, properties, {
            success: function(data) {
              console.log('FGWP: Gift added successfully', variantId);
              // Force cart drawer refresh
              if (typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
                reloadAjaxCartItemUsingCartAjaxObject(data);
              }
              // Reload cart page to show gift badge
              if (isCartPage) {
                console.log('FGWP: Reloading cart page to show gift');
                setTimeout(function() {
                  window.location.reload();
                }, 500);
              }
            },
            error: function(xhr, status, error) {
              console.error('FGWP: Unable to add gift variant', variantId, error);
            }
          });
        } else if (!shouldHaveGifts && hasGift) {
          // Remove gift from cart
          actionTaken = true;
          var giftItem = null;
          for (var i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            if (item.variant_id === variantId &&
                item.properties &&
                item.properties[FGWP_PROPERTY]) {
              giftItem = item;
              break;
            }
          }

          if (giftItem) {
            CartJS.removeItemById(giftItem.id, {
              success: function(data) {
                console.log('FGWP: Gift removed successfully');
                // Force cart drawer refresh
                if (typeof reloadAjaxCartItemUsingCartAjaxObject === 'function') {
                  reloadAjaxCartItemUsingCartAjaxObject(data);
                }
                // Reload cart page to update display
                if (isCartPage) {
                  console.log('FGWP: Reloading cart page to update display');
                  setTimeout(function() {
                    window.location.reload();
                  }, 500);
                }
              }
            });
          }
        }
      });
    } finally {
      // Release lock after operation completes
      // Longer delay if action was taken to allow cart to update
      var delay = actionTaken ? 1000 : 500;
      setTimeout(function() {
        isProcessing = false;
      }, delay);
    }
  }

  /**
   * Check cart and add/remove gifts if needed
   * Call this after CartJS is initialized or after any cart change
   */
  function checkCart() {
    console.log('FGWP: checkCart() called');
    if (typeof CartJS !== 'undefined' && CartJS.cart) {
      console.log('FGWP: CartJS available, cart items:', CartJS.cart.items.length);
      handleCartChange(CartJS.cart);
    } else {
      console.warn('FGWP: CartJS not available');
    }
  }

  /**
   * Initialize FGWP middleware
   * Subscribe to CartJS events
   */
  function init() {
    console.log('FGWP: Initializing middleware...');
    console.log('FGWP: Settings:', window.truskinSettings?.fgwp);

    // Subscribe to cart.requestComplete event (fires after any cart update)
    $(document).on('cart.requestComplete', function(event, cart) {
      console.log('FGWP: cart.requestComplete event fired', cart);
      handleCartChange(cart);
    });

    console.log('FGWP: Middleware initialized');
  }

  // Expose checkCart function globally so it can be called after CartJS.init()
  window.TruSkinFGWP = {
    checkCart: checkCart
  };

  // Initialize when script loads
  init();

})();
